import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchSessions,
  fetchStudents,
  saveAttendance,
  setSelectedSession,
  updateStudentStatus,
  updateSummary
} from '../../redux/session/attendanceSlice';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import 'bootstrap/dist/css/bootstrap.min.css';

const Attendance = () => {
  const dispatch = useDispatch();
  const { sessions, students, selectedSession, summary } = useSelector(
    (state) => state.attendance
  );

  const [activeTab, setActiveTab] = useState('day');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthRecords, setMonthRecords] = useState([]);
  const [loadingMonth, setLoadingMonth] = useState(false);

  useEffect(() => {
    dispatch(fetchSessions());
  }, [dispatch]);

  const handleSessionChange = (e) => {
    const sessionId = e.target.value;
    dispatch(setSelectedSession(sessionId));
    if (sessionId) dispatch(fetchStudents(sessionId));
  };

  const handleToggleChange = (studentId, currentStatus) => {
    const newStatus = currentStatus === 'Present' ? 'Absent' : 'Present';
    dispatch(updateStudentStatus({ id: studentId, status: newStatus }));

    dispatch(saveAttendance({ session_id: selectedSession, student_id: studentId, status: newStatus }))
      .then(() => {
        toastr.success(`Marked ${newStatus}`);
        dispatch(updateSummary());
      })
      .catch(() => {
        toastr.error('Error updating attendance!');
        dispatch(updateStudentStatus({ id: studentId, status: currentStatus }));
      });
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hour, minute] = time.split(':');
    const h = parseInt(hour, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minute} ${ampm}`;
  };

  const getDaysInMonth = (month, year) => new Date(year, month, 0).getDate();

  const handleMonthChange = async (e) => {
    const month = parseInt(e.target.value, 10);
    setSelectedMonth(month);
    setMonthRecords([]);

    if (!month) return;

    setLoadingMonth(true);

    try {
      const res = await fetch(
        `https://aadhavacademy.com/api/session/attendance.php?action=get_monthly&month=${month}&year=${selectedYear}`
      );
      const data = await res.json();

      if (Array.isArray(data)) {
        const formatted = data.map((item) => {
          const attendance = {};
          for (let d = 1; d <= 31; d++) {
            const val = item[d];
            if (val === '✔️') attendance[d] = 'P';
            else if (val === '❌') attendance[d] = 'A';
          }
          return { id: item.id, name: item.name, attendance };
        });
        setMonthRecords(formatted);
      } else {
        toastr.warning('No records found for this month');
      }
    } catch (err) {
      console.error(err);
      toastr.error('Failed to fetch monthly attendance');
    } finally {
      setLoadingMonth(false);
    }
  };

  const handleYearChange = (e) => {
    const year = parseInt(e.target.value, 10);
    setSelectedYear(year);
    if (selectedMonth) {
      // Refetch month data when year changes
      handleMonthChange({ target: { value: selectedMonth } });
    }
  };

  const daysInSelectedMonth = selectedMonth
    ? getDaysInMonth(selectedMonth, selectedYear)
    : 0;

  return (
    <div className="container mt-4 mb-5">
      <div className="card border-0 shadow-sm rounded-3">
        <div className="card-header bg-primary text-white py-2 text-center rounded-top-3">
          <h5 className="mb-0 fw-bold">Attendance Management</h5>
        </div>

        <ul className="nav nav-tabs nav-justified small fw-semibold bg-light border-bottom-0">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'day' ? 'active text-primary' : 'text-secondary'}`}
              onClick={() => setActiveTab('day')}
            >
              Day Wise
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'month' ? 'active text-primary' : 'text-secondary'}`}
              onClick={() => setActiveTab('month')}
            >
              Month Wise
            </button>
          </li>
        </ul>

        <div className="card-body p-3">
          {/* DAY WISE TAB */}
          {activeTab === 'day' && (
            <>
              <div className="row justify-content-center mb-3">
                <div className="col-md-6 col-12">
                  <label className="form-label fw-semibold text-secondary small">Select Session</label>
                  <select
                    className="form-select form-select-sm rounded-2"
                    value={selectedSession || ''}
                    onChange={handleSessionChange}
                  >
                    <option value="">-- Choose Session Date & Time --</option>
                    {sessions.map((sess) => (
                      <option key={sess.id} value={sess.id}>
                        {sess.session_date} ({formatTime(sess.start_time)} - {formatTime(sess.end_time)})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {students.length > 0 && (
                <div className="text-center mb-2">
                  <span className="badge bg-success fs-6 px-3 py-2">
                    ✅ Present: {summary?.present_count || 0} / {summary?.total_students || students.length}
                  </span>
                </div>
              )}

              {students.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm table-bordered align-middle text-center mb-0">
                    <thead className="table-primary small">
                      <tr>
                        <th>#</th>
                        <th>Student Name</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((st, index) => (
                        <tr key={st.id}>
                          <td>{index + 1}</td>
                          <td className="text-start ps-3 fw-semibold">{st.name}</td>
                          <td>
                            <div className="d-flex justify-content-center align-items-center gap-2">
                              <div className="form-check form-switch mb-0">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`switch-${st.id}`}
                                  checked={st.status === 'Present'}
                                  onChange={() => handleToggleChange(st.id, st.status)}
                                  style={{ cursor: 'pointer', transform: 'scale(1.1)' }}
                                />
                              </div>
                              <label
                                htmlFor={`switch-${st.id}`}
                                className={`fw-semibold mb-0 ${
                                  st.status === 'Present' ? 'text-success' : 'text-danger'
                                }`}
                              >
                                {st.status}
                              </label>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                selectedSession && <p className="text-center text-muted mt-3">No students found for this session.</p>
              )}
            </>
          )}

          {/* MONTH WISE TAB */}
          {activeTab === 'month' && (
            <div>
              <div className="row justify-content-center mb-3">
                <div className="col-md-3 col-5">
                  <label className="form-label fw-semibold text-secondary small">Select Month</label>
                  <select
                    className="form-select form-select-sm rounded-2"
                    value={selectedMonth}
                    onChange={handleMonthChange}
                  >
                    <option value="">-- Month --</option>
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {new Date(0, i).toLocaleString('en', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3 col-5">
                  <label className="form-label fw-semibold text-secondary small">Select Year</label>
                  <select
                    className="form-select form-select-sm rounded-2"
                    value={selectedYear}
                    onChange={handleYearChange}
                  >
                    {Array.from({ length: 5 }, (_, i) => {
                      const year = new Date().getFullYear() - i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              {loadingMonth ? (
                <p className="text-center text-primary fw-semibold">Loading attendance...</p>
              ) : monthRecords.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-bordered table-sm text-center align-middle">
                    <thead className="table-primary small">
                      <tr>
                        <th>S.No</th>
                        <th>Student Name</th>
                        {Array.from({ length: daysInSelectedMonth }, (_, i) => (
                          <th key={i + 1}>{i + 1}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {monthRecords.map((st, index) => (
                        <tr key={st.id}>
                          <td>{index + 1}</td>
                          <td className="text-start fw-semibold">{st.name}</td>
                          {Array.from({ length: daysInSelectedMonth }, (_, day) => {
                            const d = day + 1;
                            const status = st.attendance[d];
                            return (
                              <td key={d}>
                                {status === 'P' ? (
                                  <span className="text-success fw-bold">✔</span>
                                ) : status === 'A' ? (
                                  <span className="text-danger fw-bold">✖</span>
                                ) : (
                                  '-'
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}

                      <tr className="table-secondary fw-bold">
                        <td colSpan={2} className="text-end pe-2">
                          Total Present
                        </td>
                        {Array.from({ length: daysInSelectedMonth }, (_, day) => {
                          const d = day + 1;
                          const totalPresent = monthRecords.reduce(
                            (count, st) => count + (st.attendance[d] === 'P' ? 1 : 0),
                            0
                          );
                          return <td key={d}>{totalPresent || '-'}</td>;
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                selectedMonth && <p className="text-center text-muted mt-3">No records available for this month.</p>
              )}
            </div>
          )}
        </div>

        <div className="card-footer bg-light text-center py-2 small text-muted rounded-bottom-3">
          Aadhav Academy • Attendance Module
        </div>
      </div>
    </div>
  );
};

export default Attendance;
